import type { EScholarshipStatusEnum } from '@/shared/api/generated';

/**
 * Chave de i18n de cada valor de `EScholarshipStatus`.
 *
 * O mapa é derivado do enum **gerado** pelo Kubb, cuja descrição documenta os
 * seis valores (`1 = Interested`, `2 = InProcess`, `3 = ScholarshipHolder`,
 * `4 = Refused`, `5 = RefusedInterested`, `6 = RefusedPotential`) — não é um
 * mapeamento adivinhado no frontend.
 *
 * Contraste deliberado com `type` e `status` do plano, que **não** têm enum
 * gerado nem endpoint de lookup e por isso são exibidos como código cru (ver
 * `ContractHeaderCard`): aqui há fonte, lá não.
 */
const SCHOLARSHIP_STATUS_KEY = {
  1: 'planEdit.scholarshipStatus.interested',
  2: 'planEdit.scholarshipStatus.inProcess',
  3: 'planEdit.scholarshipStatus.holder',
  4: 'planEdit.scholarshipStatus.refused',
  5: 'planEdit.scholarshipStatus.refusedInterested',
  6: 'planEdit.scholarshipStatus.refusedPotential',
} as const satisfies Record<EScholarshipStatusEnum, string>;

/** Valores de `EScholarshipStatus`, na ordem em que aparecem no select. */
export const SCHOLARSHIP_STATUS_VALUES = [1, 2, 3, 4, 5, 6] as const;

/** Chave de i18n de um valor de status de bolsa. */
export type ScholarshipStatusKey =
  (typeof SCHOLARSHIP_STATUS_KEY)[keyof typeof SCHOLARSHIP_STATUS_KEY];

/**
 * Chave de i18n do status de solicitação de bolsa.
 *
 * Valor fora do enum devolve `null` em vez de string vazia, para que a UI possa
 * cair no placeholder — o backend pode introduzir um id novo antes de o
 * frontend saber dele.
 *
 * @param status - Valor inteiro vindo de `IndividualPaymentEditorViewModel.scholarshipStatus`.
 * @returns Chave de i18n, ou `null` quando o valor é ausente ou desconhecido.
 *
 * @example
 * ```ts
 * getScholarshipStatusKey(3); // 'planEdit.scholarshipStatus.holder'
 * getScholarshipStatusKey(99); // null
 * ```
 */
export function getScholarshipStatusKey(
  status: number | null | undefined,
): ScholarshipStatusKey | null {
  if (status === null || status === undefined) return null;
  return SCHOLARSHIP_STATUS_KEY[status as EScholarshipStatusEnum] ?? null;
}
